import { useCallback, useMemo } from "react";
import { PersonPicker, type DirectoryPerson, type PeopleSearch } from "@fox1ck-org/ui/saas";
import { usersApi, type KeycloakUser } from "@/api/users";
import { useUser } from "@/hooks/use-users";

/** The shape the shared picker draws, from what Keycloak calls a user. */
export function personFromUser(user: Pick<KeycloakUser, "id" | "username" | "firstName" | "lastName" | "email">): DirectoryPerson {
  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return {
    id: user.id,
    sub: user.id,
    name: fullName || user.username || user.email || user.id,
    email: user.email,
  };
}

interface UserPickerProps {
  /** The chosen Keycloak `sub`, or null for nobody. */
  value: string | null;
  onChange: (id: string | null) => void;
  /** The person behind `value` when the caller already has them; otherwise resolved from the directory. */
  selected?: DirectoryPerson | null;
  /** Ids never to list — people already on the thing. */
  exclude?: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  className?: string;
}

/**
 * Choose one user from the realm. A thin skin over `@fox1ck-org/ui/saas`
 * `PersonPicker`: the only
 * thing this app supplies is the directory — `GET /users?search=&max=30` —
 * and the picker owns debouncing, caching, aborting and the empty-query open.
 *
 * The trigger's label never lives in local state: it comes from `selected`
 * when the caller has the person, from the picker's own memory of what the
 * directory returned, and — so a remount still names them — from the user
 * detail query, which React Query keeps across remounts.
 */
export function UserPicker({ value, onChange, selected, exclude, placeholder = "Выберите пользователя…", disabled, label, className }: UserPickerProps) {
  const search = useCallback<PeopleSearch>(
    async ({ query }, signal) => {
      const { users } = await usersApi.list({ search: query || undefined, max: 30 }, signal);
      return users.map(personFromUser);
    },
    [],
  );

  const { data: resolvedUser } = useUser(selected ? "" : (value ?? ""));
  const resolved = useMemo(
    () => selected ?? (resolvedUser && resolvedUser.id === value ? personFromUser(resolvedUser) : null),
    [selected, resolvedUser, value],
  );

  return (
    <PersonPicker
      value={value}
      onChange={onChange}
      search={search}
      selected={resolved}
      exclude={exclude}
      allowClear={false}
      labels={{ placeholder, search: "Поиск по имени, логину или почте…", empty: "Пользователи не найдены." }}
      disabled={disabled}
      label={label ?? "Пользователь"}
      className={className ?? "w-full"}
      contentClassName="w-100"
    />
  );
}
